<div class="full-height">
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <div id="editor" class="p-5 container">
      <h2>Page Editor</h2>

      <div>
        <!-- Type selection -->
        @for (type of radioOptions; track type) {
          <label class="custom-padding">
            <input type="radio" formControlName="pageType" [value]="type" (change)="toggleForm(type)">
            {{ type | titlecase }}
          </label>
        }

        <!-- Common content -->
        <div>
          <div class="form-group">
            <label for="pageTitle" class="required">Page Title</label>
            <small> (Max length: 50 characters)</small>
            <input type="text" class="form-control" formControlName="pageTitle" placeholder="Page Title"
              maxlength="50" />
            </div>
            @if (pageTitle.touched && pageTitle.invalid) {
              <div class="alert alert-danger m-1 d-flex align-items-center"
                role="alert">
                <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                Please enter a valid page title.
              </div>
            }

            <!-- Additional fields for internal content -->
            @if (form.value.pageType === 'internal') {
              <div formGroupName="internalForm">
                <div class="form-group">
                  <label for="routerLink" [ngClass]="{ required: isNew }">Url Slug</label>
                  @if (isNew) {
                    <span>
                      <small> (Max length: 50 characters)</small>
                      <small>
                        (must start with "<b>/</b>", followed by
                        <b>lowercase letters</b> and <b>hyphens (-)</b> only)</small>
                      </span>
                    }
                    <input [readOnly]="!isNew" type="text" class="form-control" formControlName="routerLink"
                      placeholder="ex, '/new-page'" maxlength="50" />
                      @if (routerLink.touched && routerLink.invalid) {
                        <div
                          class="alert alert-danger m-1 d-flex align-items-center" role="alert">
                          <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                          Please enter a valid slug.
                        </div>
                      }
                    </div>
                    <div class="form-group">
                      <label for="formFile" class="form-label">Header Background Image</label>
                      <div class="input-group custom-file-button">
                        <label id="upload-btn" class="input-group-text" for="fileUpload">Choose/Change</label>
                        <input id="file-name" readonly type="text" class="form-control" formControlName="fileName"
                          [placeholder]="pageContent?.imageFileName ?? 'Select image...'" />
                          <input hidden type="file" class="form-control" id="fileUpload" formControlName="fileUpload"
                            (change)="onFileSelected($event)" />
                          </div>
                        </div>
                        <div class="form-group pb-4">
                          <label for="editorContent" class="required">Page Contents</label>
                          <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
                          <div class="NgxEditor__Wrapper">
                            <ngx-editor height="500px" [editor]="editor" [placeholder]="'Type here...'"
                              formControlName="editorContent">
                            </ngx-editor>
                          </div>
                          @if (internalForm.get('editorContent')?.touched && internalForm.get('editorContent')?.invalid) {
                            <div
                              class="alert alert-danger m-1 d-flex align-items-center" role="alert">
                              <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                              Please enter valid page content.
                            </div>
                          }
                        </div>
                      </div>
                    }

                    <!-- Additional fields for external content -->
                    @if (form.value.pageType === 'external') {
                      <div formGroupName="externalForm">
                        <div class="form-group">
                          <label for="hrefLink" class="required">External Link (URL, include http://)</label>
                          <input type="text" class="form-control mt-2" formControlName="hrefLink" placeholder="" maxlength="250" />
                          @if (form.controls.externalForm.get('hrefLink')?.hasError('required') && form.controls.externalForm.get('hrefLink')?.touched) {
                            <div
                              class="alert alert-danger m-1 d-flex align-items-center" role="alert">
                              <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                              External Link is required.
                            </div>
                          }
                          @if (form.controls.externalForm.get('hrefLink')?.hasError('maxlength') && form.controls.externalForm.get('hrefLink')?.touched) {
                            <div
                              class="alert alert-danger m-1 d-flex align-items-center" role="alert">
                              <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                              External Link must be at most 250 characters long.
                            </div>
                          }
                          @if (form.controls.externalForm.get('hrefLink')?.hasError('pattern') && form.controls.externalForm.get('hrefLink')?.touched) {
                            <div
                              class="alert alert-danger m-1 d-flex align-items-center" role="alert">
                              <fa-icon [icon]="faExclamationTriangle" style="margin-right: 0.5rem !important"></fa-icon>
                              Please enter a valid URL.
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Common section -->
                  @if (!isNew && !showExternalContent) {
                    <div>
                      @if (pageContent?.sections) {
                        <div class="custom-section">
                          <hr />
                          @for (section of pageContent?.sections; track section; let i = $index) {
                            <div class="py-4">
                              @if (section.sectionTypeId == SectionType.ProfileBio) {
                                <div>
                                  <div class="d-flex justify-content-between section-header">
                                    <h2>{{ section.name }}</h2>
                                    <div>
                                      <button class="btn btn-primary btn-sm" type="button"
                                        (click)="addNewOrSave(AddType.ProfileBio, section.id)">
                                        Upload Profile Bio <fa-icon [icon]="faUpload"></fa-icon>
                                      </button>
                                    </div>
                                  </div>
                                  <app-drag-drop-multi-direction [items]="section.profileBios"
                                    (arrayChanged)="updateProfileOrder($event, i)" (deleteIconClick)="handleProfileDelete($event)"
                                    (editIconClick)="addNewOrSave(AddType.ProfileBio, section.id, $event)" [configuration]="configuration"
                                    [isEditable]="section.isEditable">
                                  </app-drag-drop-multi-direction>
                                </div>
                              } @else {
                                @if (section.sectionTypeId == SectionType.Sponsor) {
                                  <div>
                                    <div class="d-flex justify-content-between section-header">
                                      <h2>{{ section.name }}</h2>
                                      <div>
                                        <button class="btn btn-primary btn-sm" type="button"
                                          (click)="addNewOrSave(AddType.Sponsor, section.id)">
                                          Upload Sponsor <fa-icon [icon]="faUpload"></fa-icon>
                                        </button>
                                      </div>
                                    </div>
                                    <app-drag-drop-multi-direction [items]="section.sponsors"
                                      (arrayChanged)="updateSponsorOrder($event, i)" (deleteIconClick)="handleSponsorDelete($event)"
                                      (editIconClick)="addNewOrSave(AddType.Sponsor, section.id, $event)" [configuration]="configuration">
                                    </app-drag-drop-multi-direction>
                                  </div>
                                } @else {
                                  <div class="d-flex justify-content-between section-header">
                                    <h2>{{ section.name }}</h2>
                                    <div>
                                      <button class="btn btn-primary btn-sm" type="button"
                                        (click)="addNewOrSave(AddType.Files, section.id)">
                                        Upload File <fa-icon [icon]="faUpload"></fa-icon>
                                      </button>
                                    </div>
                                  </div>
                                  <app-drag-drop-multi-direction [items]="section.pdfFiles" (arrayChanged)="updateFilesOrder($event, i)"
                                    (deleteIconClick)="handleFileDelete($event)"
                                    (editIconClick)="addNewOrSave(AddType.Files, section.id, $event)" [configuration]="configuration">
                                  </app-drag-drop-multi-direction>
                                }
                              }
                            </div>
                          }
                        </div>
                      }
                      <button class="btn btn-primary btn-sm" type="button" (click)="addSection()">
                        Add New Section
                      </button>
                    </div>
                  }
                </div>
              </div>

              <!-- Common buttons -->
              <div class="sticky-bar d-flex justify-content-end">
                @if (isNew) {
                  <button type="button" class="btn btn-secondary btn-sm"
                    [disabled]="form.pristine && !form.dirty" (click)="confirmDiscard()">
                    {{ "Cancel" + (form.dirty ? " & Discard" : "") }}
                  </button>
                } @else {
                  <button type="button" class="btn btn-secondary btn-sm" (click)="confirmDiscard()"
                    [disabled]="form.pristine && !form.dirty">
                    {{ "Cancel" + (!form.pristine && form.dirty ? " & Discard" : "") }}
                  </button>
                }
                <button type="submit" class="btn btn-primary btn-sm" [disabled]="!isFormValid()">
                  {{ showExternalContent ? "Save External Link" : (isNew ? "Save and Add New Page" : "Save Changes") }}
                </button>
              </div>
            </form>
          </div>
